import type { ModuleMeta } from '../../core/types';
import { MI_CAPABILITIES } from './capabilities';
import type { MiFieldKey } from './fields';

function miBinding<K extends (typeof MI_CAPABILITIES)[number]['key']>(key: K) {
  const binding = MI_CAPABILITIES.find((item) => item.key === key);
  if (!binding) {
    throw new Error(`MI capability binding not found: ${key}`);
  }
  return binding;
}

export const MI_MODULES = [
  {
    key: 'baseInfo',
    title: '基本信息',
    capabilities: [miBinding('base-info')],
    fields: ['base.name', 'base.description', 'base.workspace'],
    /** MI preset 为表单直序列化；与 MI_SKU_MODULE_META 对齐 */
    presetCheckFields: ['alias'],
  },
  {
    key: 'runtimeConfig',
    title: '运行配置',
    capabilities: [miBinding('runtime-config')],
    fields: ['mi.runtime.type', 'mi.runtime.workload', 'mi.deployMode'],
    presetCheckFields: ['runtime.type', '_inferencePdSeparation'],
    resetFieldPaths: ['runtime', '_inferencePdSeparation'],
  },
  {
    key: 'modelStorage',
    title: '模型与存储',
    capabilities: [miBinding('maas-model-storage')],
    fields: [
      'mi.model',
      'mi.model.name',
      'mi.model.mountPath',
      'mi.model.size',
      'mi.modelCategory',
      'mi.primaryDataVolume',
      'mi.storageConfigured',
    ],
    presetCheckFields: ['model', 'model.name', '_primaryDataVolume', '_modelCategory'],
    resetFieldPaths: ['model', '_primaryDataVolume', '_modelCategory'],
  },
  {
    key: 'systemConfig',
    title: '系统配置',
    capabilities: [miBinding('maas-system-config')],
    fields: ['mi.framework', 'mi.inferenceSource', 'mi.inferenceSystem', 'mi.inferenceFrameworkImage'],
    presetCheckFields: ['framework', 'maasInferenceSource', '_inferenceSystem', 'inferenceFrameworkImage'],
    resetFieldPaths: [
      'framework',
      'maasInferenceSource',
      '_inferenceSystem',
      'inferenceFrameworkImage',
      '_imageContainerDeployMountPath',
      '_imageContainerDeployInstanceScheduling',
      '_imageContainerDeployEnvVars',
      '_imageContainerDeployApis',
    ],
  },
  {
    key: 'healthCheck',
    title: '探活',
    capabilities: [miBinding('health-check')],
    fields: ['healthCheck.config'],
    presetCheckFields: ['_containerDeployHealthCheck'],
    resetFieldPaths: ['_containerDeployHealthCheck'],
  },
  {
    key: 'service',
    title: '服务入口',
    capabilities: [miBinding('service')],
    fields: ['service.list'],
    presetCheckFields: ['_inferenceService', '_serviceMountGroupName'],
    resetFieldPaths: ['_inferenceService', '_serviceMountGroupName'],
  },
  {
    key: 'storage',
    title: '镜像存储',
    capabilities: [miBinding('maas-image-storage')],
    fields: ['mi.imageStorageVolumes'],
    presetCheckFields: ['_imageStorageVolumes', '_appStorageGroups', '_primaryDataVolume', 'model.size'],
    resetFieldPaths: ['_imageStorageVolumes'],
  },
  {
    key: 'nodeGroups',
    title: '节点配置',
    capabilities: [miBinding('maas-node-groups')],
    fields: ['mi.runMode', 'mi.masterNodes', 'mi.workerNodes', 'mi.masterSystemDisk', 'mi.workerSystemDisk', 'mi.replicas'],
    presetCheckFields: [
      '_inferencePdSeparation',
      'masterNodes',
      'workerNodes',
      'runMode',
      'commonEnv',
      'replicas',
      'prefillMasterNodes',
      'prefillWorkerNodes',
      'decodeMasterNodes',
      'decodeWorkerNodes',
      'prefillReplicas',
      'decodeReplicas',
    ],
    resetFieldPaths: [
      'runMode',
      'mode',
      'replicas',
      'taskTemplate',
      'commonEnv',
      'masterNodes',
      'workerNodes',
      'prefillReplicas',
      'prefillTaskTemplate',
      'prefillRunMode',
      'prefillMasterNodes',
      'prefillWorkerNodes',
      'decodeReplicas',
      'decodeTaskTemplate',
      'decodeRunMode',
      'decodeMasterNodes',
      'decodeWorkerNodes',
      'master',
      'worker',
      'prefill.master.systemDiskSize',
      'prefill.worker.systemDiskSize',
      'decode.master.systemDiskSize',
      'decode.worker.systemDiskSize',
      'master.systemDiskSize',
      'worker.systemDiskSize',
      'masterSystemDiskPricing',
      'workerSystemDiskPricing',
      'prefillMasterSystemDiskPricing',
      'prefillWorkerSystemDiskPricing',
      'decodeMasterSystemDiskPricing',
      'decodeWorkerSystemDiskPricing',
      'prefillCommonEnv',
      'decodeCommonEnv',
    ],
  },
  {
    key: 'gatewayConfig',
    title: '网关组件配置',
    capabilities: [miBinding('maas-gateway-nodes')],
    fields: ['mi.gatewayNodes', 'mi.gatewaySystemDisk'],
    presetCheckFields: ['gatewayNodes'],
    resetFieldPaths: ['gatewayNodes', 'gateway.systemDiskSize', 'gatewaySystemDiskPricing'],
  },
  {
    key: 'kvOffload',
    title: '缓存组件配置',
    capabilities: [miBinding('mi-kv-offload')],
    fields: [],
    presetCheckFields: ['kvOffloadMasterNodes', 'kvOffloadWorkerNodes', 'kvOffloadRunMode', 'kvOffloadReplicas'],
    resetFieldPaths: [
      'kvOffloadReplicas',
      'kvOffloadTaskTemplate',
      'kvOffloadRunMode',
      'kvOffloadMasterNodes',
      'kvOffloadWorkerNodes',
      'kvOffload.master.systemDiskSize',
      'kvOffload.worker.systemDiskSize',
      'kvOffloadMasterSystemDiskPricing',
      'kvOffloadWorkerSystemDiskPricing',
      'kvOffloadCommonEnv',
    ],
  },
  {
    key: 'nic',
    title: '网卡',
    capabilities: [],
    fields: ['mi.network.nics', 'mi.network.nicPricing'],
    presetCheckFields: ['network.nics'],
    resetFieldPaths: ['network.nics', 'network.nicPricing'],
  },
  {
    key: 'taskConfig',
    title: '任务配置',
    capabilities: [miBinding('maas-task-template')],
    fields: ['mi.taskTemplate.list'],
    resetFieldPaths: ['taskTemplate'],
  },
] as const satisfies readonly ModuleMeta<MiFieldKey>[];

export const MI_MODULE_MAP = Object.fromEntries(MI_MODULES.map((item) => [item.key, item]));
